// Hyperliquid-WS pure functions. 1:1 portiert aus V1 useHyperliquidGlobal.js.
// - Parser: parseAllMids, parseAssetCtx, parseTrade
// - Whale-Detection: detectWhale (Filter sizeUsd >= 1_000_000)
// - buildSubscribeMessages: Array von Subscribe-Payloads für connect()

import type {
  HLAssetCtx,
  HLTradeTick,
  HLWsInbound,
  WhaleAlert,
} from '@/types/hyperliquid';

export const HL_WS_URL = 'wss://api.hyperliquid.xyz/ws';

/** V1: Whale-Threshold. */
export const WHALE_THRESHOLD_USD = 1_000_000;

/** V1: 1s-Drosselung für React-State-Updates. */
export const STATE_UPDATE_THROTTLE_MS = 1000;

/** V1: Reconnect-Delay. */
export const RECONNECT_DELAY_MS = 3000;

/** V1: Standard-Coins die der Global-Tracker abonniert (Crypto + Commodities). */
export const SUBSCRIBED_COINS = [
  'BTC',
  'ETH',
  'SOL',
  'xyz:GOLD',
  'xyz:SILVER',
] as const;

/** Baut die Subscribe-Messages für `connect()` (V1: allMids + 2 Subs pro Coin). */
export function buildSubscribeMessages(): string[] {
  const messages: string[] = [JSON.stringify({ method: 'subscribe', subscription: { type: 'allMids' } })];
  for (const coin of SUBSCRIBED_COINS) {
    messages.push(JSON.stringify({ method: 'subscribe', subscription: { type: 'trades', coin } }));
    messages.push(JSON.stringify({ method: 'subscribe', subscription: { type: 'activeAssetCtx', coin } }));
  }
  return messages;
}

const num = (v: unknown): number => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

/**
 * Parsed eine `activeAssetCtx`-Message. V1-Verhalten:
 * - `change` = (mark-prev)/prev * 100, 2 Nachkommastellen
 * - alle Felder default 0 wenn nicht parseable
 */
export function parseAssetCtx(msg: HLWsInbound): HLAssetCtx | null {
  if (msg.channel !== 'activeAssetCtx') return null;
  const data = msg.data as { coin?: string; ctx?: Record<string, unknown> } | undefined;
  if (!data?.coin || !data.ctx) return null;
  const ctx = data.ctx;
  const mark = num(ctx['markPx']);
  const prev = num(ctx['prevDayPx']);
  const change = mark > 0 && prev > 0 ? parseFloat((((mark - prev) / prev) * 100).toFixed(2)) : 0;
  return {
    coin: data.coin,
    funding: num(ctx['funding']),
    openInterest: num(ctx['openInterest']),
    markPx: mark,
    oraclePx: num(ctx['oraclePx']),
    premium: num(ctx['premium']),
    change,
  };
}

/** Parsed eine `allMids`-Message. Gibt die mids-Map zurück (oder null). */
export function parseAllMids(msg: HLWsInbound): Record<string, string> | null {
  if (msg.channel !== 'allMids') return null;
  const data = msg.data as { mids?: Record<string, string> } | undefined;
  return data?.mids ?? null;
}

/**
 * Konvertiert einen Trade-Tick in einen Whale-Alert (oder null wenn unter Threshold).
 * V1-Verhalten:
 * - sizeUsd = px * sz
 * - side: 'B' → BUY, sonst SELL
 * - wallet: erste users-Adresse oder null
 */
export function detectWhale(t: HLTradeTick, fallbackCoin?: string): WhaleAlert | null {
  const price = num(t.px);
  const size = num(t.sz);
  const sizeUsd = price * size;
  if (sizeUsd < WHALE_THRESHOLD_USD) return null;
  return {
    coin: t.coin ?? fallbackCoin ?? 'UNKNOWN',
    price,
    size,
    sizeUsd,
    side: t.side === 'B' ? 'BUY' : 'SELL',
    time: t.time,
    hash: t.hash ?? null,
    wallet: t.users?.[0] ?? null,
  };
}

/** Wendet detectWhale auf eine ganze trades-Message an. */
export function parseTradesForWhales(msg: HLWsInbound): WhaleAlert[] {
  if (msg.channel !== 'trades') return [];
  const ticks = msg.data as HLTradeTick[] | undefined;
  if (!Array.isArray(ticks) || ticks.length === 0) return [];
  const fallbackCoin = ticks[0]?.coin;
  const out: WhaleAlert[] = [];
  for (const t of ticks) {
    const whale = detectWhale(t, fallbackCoin);
    if (whale) out.push(whale);
  }
  return out;
}

/**
 * Sicheres JSON-Parsing — gibt `null` zurück bei Parse-Fehler statt zu werfen
 * (V1-Verhalten: silent try/catch in onmessage).
 */
export function safeParseMessage(raw: string): HLWsInbound | null {
  try {
    return JSON.parse(raw) as HLWsInbound;
  } catch {
    return null;
  }
}
